import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tagsApi } from './tagsApi';
import type { TagSummary } from '@/generated/api/models/tagSummary';

/** A taggable entity reference: `{module}.{entity}` type plus the entity id. */
export interface TaggableEntityRef {
  entityType: string;
  entityId: string;
}

/** Stable `${entityType}:${entityId}` key for one ref. */
export function entityTagsKey(entityType: string, entityId: string): string {
  return `${entityType}:${entityId}`;
}

/**
 * Batch-resolve the tags for many entities (possibly of mixed types) in a
 * single request. Backs the AI-chat entity-reference chips, where a transcript
 * may reference several deals, contacts and tickets at once.
 *
 * Returns a Map keyed by `entityTagsKey(entityType, entityId)`.
 */
export function useEntityTagsBatch(
  refs: TaggableEntityRef[],
): Map<string, TagSummary[]> {
  // Stable, deduplicated, sorted ref-string list so the query key is stable.
  const refStrings = useMemo(() => {
    const seen = new Set<string>();
    for (const r of refs) {
      seen.add(entityTagsKey(r.entityType, r.entityId));
    }
    return [...seen].sort();
  }, [refs]);

  const { data } = useQuery({
    queryKey: ['entity-tags-batch', refStrings],
    queryFn: () => tagsApi.batchForEntities(refStrings),
    enabled: refStrings.length > 0,
    staleTime: 60_000,
  });

  return useMemo(() => {
    const map = new Map<string, TagSummary[]>();
    for (const item of data?.items ?? []) {
      if (item.tags.length > 0) {
        map.set(entityTagsKey(item.entity_type, item.entity_id), item.tags);
      }
    }
    return map;
  }, [data]);
}
